import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import { Tracker } from '../../domains/analytics';
import { TABLE_ADVANCED } from '../../domains/analytics/constants/MetricEvents';

export type TableDockTarget = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

export type DockNodeToTableCellParams =
  | { mode: 'dock'; dock: TableDockTarget }
  | { mode: 'undock' };

export class DockNodeToTableCellCommand extends BaseNodeCommand {
  constructor(
    private nodeId: string,
    private newPosition: { x: number; y: number },
    private oldPosition?: { x: number; y: number },
    private params?: DockNodeToTableCellParams,
  ) {
    super('Dock Node To Cell', 'Dock node into a table cell');
  }

  protected doExecute(): NodeChangeRecord[] {
    const record = this.getMutationManager().applyNodeChange(this.nodeId, {
      x: this.newPosition.x,
      y: this.newPosition.y,
      attrs: {
        tableDock: this.params?.mode === 'dock' ? (this.params as any).dock : undefined,
      } as any,
    } as any);

    if (record && this.oldPosition && this.changeRecords.length === 0) {
      record.oldState = { ...record.oldState, x: this.oldPosition.x, y: this.oldPosition.y };
      const prevAttrs = (record.oldState as any)?.attrs ?? {};
      const prevDock = (prevAttrs as any)?.tableDock;
      record.oldState = {
        ...record.oldState,
        attrs: { ...prevAttrs, tableDock: prevDock },
      } as any;
    }

    // Track content dropped when docking (not undocking)
    if (record && this.params?.mode === 'dock') {
      try {
        const mutationManager = this.getMutationManager();
        const node = mutationManager.getNode(this.nodeId);
        if (node) {
          const nodeType = (node as any).type;
          const attrs = (node.attrs ?? {}) as Record<string, unknown>;
          const category = typeof attrs.category === 'string' ? attrs.category : undefined;

          const diagramId =
            typeof window !== 'undefined'
              ? ((window as unknown as { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__ ?? 'demo-diagram')
              : 'demo-diagram';

          let contentType: 'sticky' | 'shape' | 'image' | 'card' = 'shape';
          if (nodeType === 'sticky-note' || category === 'sticky-note') {
            contentType = 'sticky';
          }

          Tracker.track(TABLE_ADVANCED.CONTENT_DROPPED, {
            tableId: this.params.dock.tableId,
            diagramId,
            contentType,
            cellId: this.params.dock.cellKey,
          });
        }
      } catch {
        // Silently fail - tracking is best-effort
      }
    }

    return record ? [record] : [];
  }
}

